import { ViewBody } from 'components/Sidebar'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import DocumentList from './DocumentList'
import styled from 'styled-components'
import Scrollbars from 'components/Scrollbars'
import Alert from 'components/Alert'
import DocumentItem from './DocumentItem'
import Loader from 'components/Loader'
import fetchDocumentsSourcesInBoard from '../sagas/fetchDocumentsSourcesInBoard'
import { useDispatch, useSelector } from 'react-redux'
import { useUncontrolled } from 'uncontrollable'
import PathBreadcrumb from 'components/PathBreadcrumb'

const PathContainer = styled(ViewBody)`
  margin-top: 5px;
  margin-bottom: 10px;
`

const DocumentTemplatesListScrollbars = styled(Scrollbars)`
  display: flex;
  flex-direction: column;
`

type Ref = {
  refresh: () => void;
}

type Props = {
  boardId: number;
  autoSelectDocumentSource?: boolean;
  query?: string;
  documentComponent?: React.ComponentType<React.ComponentProps<typeof DocumentItem>>;
  onDocumentSourceChange?: (documentSourceId: number) => void;

  path?: any[];
  onPathChange?: (path: any[]) => void;
}

const Documents = React.forwardRef<Ref, Props>((props, ref) => {
  const {
    boardId,
    autoSelectDocumentSource,
    query,
    documentComponent: DocumentComponent = DocumentItem,
    onDocumentSourceChange,

    path = [],
    onPathChange,
  } = useUncontrolled(props, {
    path: 'onPathChange',
  })

  const selectedDefaultSource = useRef<boolean>(false)

  const dispatch = useDispatch()

  const documentListRef = useRef<any>()

  const documentSourcesLoaded = useSelector<any, boolean>(state => state['view-board'].documentSources[boardId]?.isLoaded)
  const documentSourcesLoading = useSelector<any, boolean>(state => state['view-board'].documentSources[boardId]?.isLoading)
  const defaultDocumentSourceId = useSelector<any, number>(state => state['view-board'].documentSources[boardId]?.defaultDocumentSource)
  const documentSourceIds = useSelector<any, number[]>(state => state['view-board'].documentSources[boardId]?.documentSources)
  const documentSourcesById = useSelector<any, any>(state => state['view-board'].documentSources.documentSourcesById)

  const refresh = useCallback(() => {
    if (path.length > 0) {
      documentListRef.current?.refresh()
    } else {
      dispatch(fetchDocumentsSourcesInBoard({
        boardId,
      }))
    }
  }, [path, boardId])

  useImperativeHandle(ref, () => ({
    refresh
  }))

  useEffect(() => {
    if (!documentSourcesLoaded && !documentSourcesLoading) {
      dispatch(fetchDocumentsSourcesInBoard({
        boardId,
      }))
    }
  }, [documentSourcesLoaded, documentSourcesLoading])

  useEffect(() => {
    if (
      autoSelectDocumentSource &&
      selectedDefaultSource.current === false &&
      documentSourcesLoaded &&
      documentSourceIds.length > 1 &&
      documentSourcesById[defaultDocumentSourceId]
    ) {
      selectedDefaultSource.current = true

      onPathChange?.([
        {
          documentSourceId: defaultDocumentSourceId,
          name: documentSourcesById[defaultDocumentSourceId].name,
        }
      ])
    }
  }, [autoSelectDocumentSource, documentSourcesLoaded, defaultDocumentSourceId, documentSourceIds, documentSourcesById])

  const documentListSourceId = useMemo(() => {
    if (
      autoSelectDocumentSource &&
      documentSourcesLoaded &&
      documentSourceIds.length === 1
    ) {
      return documentSourceIds[0]
    } else if (path.length > 0) {
      return path[0].documentSourceId
    }

    return undefined
  }, [autoSelectDocumentSource, documentSourcesLoaded, documentSourceIds, path])

  useEffect(() => {
    onDocumentSourceChange?.(documentListSourceId)
  }, [documentListSourceId])

  return (
    documentSourcesLoaded ? (
      documentSourceIds.length > 0 ? (
        <DocumentTemplatesListScrollbars>
          {(path.length > 0 || documentListSourceId) ? (
            <DocumentList
              ref={documentListRef}
              documentComponent={DocumentComponent}
              documentSourceId={documentListSourceId}
              query={query}
              title={null}
              path={path}
              onPathChange={onPathChange}
            />
          ) : (
            <>
              <PathContainer>
                <PathBreadcrumb />
              </PathContainer>
              {documentSourceIds.map((documentSourceId) =>
                <DocumentComponent
                  key={documentSourceId}
                  type='source'
                  documentSourceId={documentSourceId}
                  name={documentSourcesById[documentSourceId].name}
                  fullUrl={documentSourcesById[documentSourceId].url}
                  onClick={() => {
                    onPathChange?.([
                      {
                        documentSourceId: documentSourceId,
                        name: documentSourcesById[documentSourceId].name,
                      }
                    ])
                  }}
                />
              )}
            </>
          )}
        </DocumentTemplatesListScrollbars>
      ) : (
        <ViewBody>
          <Alert>
            <FormattedMessage id='DOCUMENTS_SOURCE_NO_DOCUMENT_SOURCES' />
          </Alert>
        </ViewBody>
      )
    ) : <Loader center />
  )
})

export default Documents
